import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { financeiroApi } from '../../../services/api';
import type { LancamentoFinanceiro, ContaBancaria, CategoriaFinanceira, Client, TipoTransacao, FormaPagamento } from '../../../types';

interface LancamentoFormProps {
  mode: 'create' | 'edit';
}

const LancamentoForm: React.FC<LancamentoFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [categorias, setCategorias] = useState<CategoriaFinanceira[]>([]);
  const [beneficiarios, setBeneficiarios] = useState<Client[]>([]);

  const [formData, setFormData] = useState({
    numero: '',
    tipo: 'D' as TipoTransacao,
    categoria: '',
    beneficiario: '',
    conta_bancaria: '',
    valor: '',
    data_vencimento: '',
    descricao: '',
    observacoes: '',
    forma_pagamento: '' as FormaPagamento | '',
    numero_documento: '',
  });

  // Load initial data
  useEffect(() => {
    loadListas();
    if (isEdit && id) {
      loadLancamento(id);
    }
  }, [isEdit, id]);

  const loadListas = async () => {
    try {
      const [contasRes, categoriasRes, beneficiariosRes] = await Promise.all([
        financeiroApi.getContas({ ativa: true, page_size: 1000 }),
        financeiroApi.getCategorias({ ativa: true, page_size: 1000 }),
        financeiroApi.getBeneficiarios({ page_size: 1000 }),
      ]);

      setContas(contasRes.results || []);
      setCategorias(categoriasRes.results || []);
      setBeneficiarios(beneficiariosRes.results || []);
    } catch (error) {
      console.error('Error loading lists:', error);
    }
  };

  const loadLancamento = async (lancamentoId: string) => {
    try {
      setLoading(true);
      const lancamento = await financeiroApi.getLancamento(Number(lancamentoId));
      setFormData({
        numero: lancamento.numero || '',
        tipo: lancamento.tipo || 'D',
        categoria: lancamento.categoria?.toString() || '',
        beneficiario: lancamento.beneficiario?.toString() || '',
        conta_bancaria: lancamento.conta_bancaria?.toString() || '',
        valor: lancamento.valor?.toString() || '',
        data_vencimento: lancamento.data_vencimento || '',
        descricao: lancamento.descricao || '',
        observacoes: lancamento.observacoes || '',
        forma_pagamento: lancamento.forma_pagamento || '',
        numero_documento: lancamento.numero_documento || '',
      });
    } catch (error) {
      console.error('Error loading lancamento:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Formata valor monetário: últimos 2 dígitos são sempre decimais (formato BR: vírgula)
  const formatarValorMonetario = (valor: string): string => {
    const apenasNumeros = valor.replace(/\D/g, '');
    if (!apenasNumeros) return '';
    const valorNumerico = parseInt(apenasNumeros, 10) / 100;
    return valorNumerico.toFixed(2).replace('.', ',');
  };

  // Converte valor BR (vírgula) para formato numérico (ponto) para envio ao backend
  const valorParaNumero = (valor: string): number => {
    if (!valor) return 0;
    return parseFloat(valor.replace(',', '.')) || 0;
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = formatarValorMonetario(e.target.value);
    setFormData(prev => ({ ...prev, valor: valorFormatado }));
    if (errors.valor) {
      setErrors(prev => ({ ...prev, valor: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    if (!formData.valor || valorParaNumero(formData.valor) <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.data_vencimento) {
      newErrors.data_vencimento = 'Data de vencimento é obrigatória';
    }

    if (!formData.categoria) {
      newErrors.categoria = 'Categoria é obrigatória';
    }

    if (!formData.conta_bancaria) {
      newErrors.conta_bancaria = 'Conta bancária é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        descricao: formData.descricao,
        tipo: formData.tipo,
        valor: valorParaNumero(formData.valor),
        data_vencimento: formData.data_vencimento,
        categoria: parseInt(formData.categoria),
        conta_bancaria: parseInt(formData.conta_bancaria),
        observacoes: formData.observacoes || undefined,
        forma_pagamento: formData.forma_pagamento || undefined,
        numero_documento: formData.numero_documento || undefined,
      };

      if (formData.beneficiario) {
        payload.beneficiario = parseInt(formData.beneficiario);
      }

      if (isEdit && id) {
        await financeiroApi.updateLancamento(Number(id), payload);
      } else {
        await financeiroApi.createLancamento(payload);
      }
      navigate('/financeiro/lancamentos');
    } catch (error: any) {
      console.error('Error saving lancamento:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setSaving(false);
    }
  };

  // Filter categorias by tipo
  const categoriasFiltradas = categorias.filter(cat => cat.tipo === formData.tipo);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-1">
        <h1 className="text-base font-bold text-secondary-900">
          {isEdit ? 'Editar Lançamento' : 'Novo Lançamento'}
        </h1>
        <p className="text-xs text-secondary-600">
          {isEdit ? 'Atualize as informações do lançamento' : 'Cadastre um novo lançamento financeiro'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-1">
        {/* Informações Básicas */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Informações Básicas</h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-1">
            {isEdit && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                  Número
                </label>
                <input
                  type="text"
                  value={formData.numero}
                  disabled
                  className="input-field bg-gray-100"
                />
              </div>
            )}

            <div className={isEdit ? 'md:col-span-2' : 'md:col-span-3'}>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Tipo *
              </label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleChange}
                className={`input-field ${errors.tipo ? 'border-red-300' : ''}`}
                required
              >
                <option value="R">Receita</option>
                <option value="D">Despesa</option>
                <option value="T">Transferência</option>
              </select>
              {errors.tipo && <p className="text-red-600 text-xs mt-0.5">{errors.tipo}</p>}
            </div>

            <div className={isEdit ? 'md:col-span-2' : 'md:col-span-3'}>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Valor *
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="valor"
                value={formData.valor}
                onChange={handleValorChange}
                placeholder="0,00"
                className={`input-field ${errors.valor ? 'border-red-300' : ''}`}
                required
              />
              {errors.valor && <p className="text-red-600 text-xs mt-0.5">{errors.valor}</p>}
            </div>

            <div className="md:col-span-6">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Descrição *
              </label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                className={`input-field ${errors.descricao ? 'border-red-300' : ''}`}
                required
              />
              {errors.descricao && <p className="text-red-600 text-xs mt-0.5">{errors.descricao}</p>}
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Data de Vencimento *
              </label>
              <input
                type="date"
                name="data_vencimento"
                value={formData.data_vencimento}
                onChange={handleChange}
                className={`input-field ${errors.data_vencimento ? 'border-red-300' : ''}`}
                required
              />
              {errors.data_vencimento && <p className="text-red-600 text-xs mt-0.5">{errors.data_vencimento}</p>}
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Número do Documento
              </label>
              <input
                type="text"
                name="numero_documento"
                value={formData.numero_documento}
                onChange={handleChange}
                className="input-field"
                placeholder="Ex: NF 1234"
              />
            </div>
          </div>
        </div>

        {/* Classificação */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Classificação</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Categoria *
              </label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                className={`input-field ${errors.categoria ? 'border-red-300' : ''}`}
                required
              >
                <option value="">Selecione...</option>
                {categoriasFiltradas.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.caminho_completo || cat.nome}
                  </option>
                ))}
              </select>
              {errors.categoria && <p className="text-red-600 text-xs mt-0.5">{errors.categoria}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Beneficiário
              </label>
              <select
                name="beneficiario"
                value={formData.beneficiario}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Selecione...</option>
                {beneficiarios.map(ben => (
                  <option key={ben.id} value={ben.id}>
                    {ben.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Conta Bancária *
              </label>
              <select
                name="conta_bancaria"
                value={formData.conta_bancaria}
                onChange={handleChange}
                className={`input-field ${errors.conta_bancaria ? 'border-red-300' : ''}`}
                required
              >
                <option value="">Selecione...</option>
                {contas.map(conta => (
                  <option key={conta.id} value={conta.id}>
                    {conta.nome} - {conta.banco}
                  </option>
                ))}
              </select>
              {errors.conta_bancaria && <p className="text-red-600 text-xs mt-0.5">{errors.conta_bancaria}</p>}
            </div>
          </div>
        </div>

        {/* Pagamento */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Informações de Pagamento</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                Forma de Pagamento
              </label>
              <select
                name="forma_pagamento"
                value={formData.forma_pagamento}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Selecione...</option>
                <option value="PIX">PIX</option>
                <option value="TED">TED</option>
                <option value="DOC">DOC</option>
                <option value="BOLETO">Boleto</option>
                <option value="CARTAO_CREDITO">Cartão de Crédito</option>
                <option value="CARTAO_DEBITO">Cartão de Débito</option>
                <option value="DINHEIRO">Dinheiro</option>
                <option value="CHEQUE">Cheque</option>
                <option value="OUTROS">Outros</option>
              </select>
            </div>
          </div>
        </div>

        {/* Observações */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Observações</h3>
          <div>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleChange}
              rows={2}
              className="input-field"
              placeholder="Observações adicionais sobre o lançamento..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-1 mt-2">
          <button
            type="button"
            onClick={() => navigate('/financeiro/lancamentos')}
            className="btn-secondary"
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvando...
              </div>
            ) : (
              isEdit ? 'Atualizar Lançamento' : 'Criar Lançamento'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LancamentoForm;
